// axios
import axios from 'axios'
import Vue from 'vue'
import { applyReadOnlyHeader, setReadOnlyUser, notifyReadOnlyOnce } from '@/helpers/roleAccess.js'
// Dev  (npm run serve)  → '/api'  (proxifié vers xylo.codesinn.com par vue.config.js
//                                  devServer.proxy → pas de CORS)
// Prod (npm run build)  → URL absolue de prod
const baseURL = process.env.VUE_APP_API_URL
  || (process.env.NODE_ENV === 'development'
    ? '/api'
    : 'https://xylo.codesinn.com/api')

const instance = axios.create({
  baseURL,
  timeout: 60000
})

const AUTH_LOCALSTORAGE_KEYS = [
  'accessToken',
  'login-id',
  'login-username',
  'login-avatar',
  'login-email',
  'login-roles',
  'login-language',
  'login-broker',
  'login-date',
  'login-readonly',
  // 2FA login-state flags — transient, re-fetched at next sign-in.
  'xylo:must-enable-2fa',
  'xylo:twofa-status',
  'xylo:twofa-setup-required'
  // NOTE: 'xylo:trusted-device-token' is intentionally NOT cleared here.
  // The access token expires daily; clearing the trusted-device token on that
  // 401 would force a fresh 2FA challenge on every re-login, defeating the
  // "trust this device for N days" feature. It must survive the access-token
  // expiry so 2FA stays skipped for the full trusted-device window. It is
  // cleared server-side (token deleted) on password change / 2FA disable.
]

// Inject le JWT dans chaque requête API. Le backend lit ce header
// (verifyToken middleware) ; sans lui toutes les routes protégées renvoient 403.
// On joint aussi le trusted-device token : le backend l'utilise sur /signin
// pour sauter le challenge 2FA (familier GitHub/bank UX).
instance.interceptors.request.use(
  config => {
    if (typeof localStorage !== 'undefined') {
      const token = localStorage.getItem('accessToken')
      if (token) config.headers['x-access-token'] = token
      const td = localStorage.getItem('xylo:trusted-device-token')
      if (td) config.headers['x-trusted-device'] = td
    }
    return config
  },
  error => Promise.reject(error)
)

instance.interceptors.response.use(
  response => {
    // Drapeau « lecture seule » : suit le serveur à la requête près (en-tête
    // x-read-only posé par roleGate), dans les deux sens, sans se reconnecter.
    applyReadOnlyHeader(response && response.headers)
    return response
  },
  error => {
    const resp = error && error.response
    if (resp) applyReadOnlyHeader(resp.headers)
    if (resp && resp.status === 403 && resp.data && resp.data.code === 'read_only_user') {
      // Compte en lecture seule (Broker Status) : un seul toast neutre ; l'erreur
      // continue vers l'appelant, qui garde sa propre gestion.
      setReadOnlyUser(true)
      notifyReadOnlyOnce(Vue.prototype, resp.data.message)
    }
    if (error && error.response && error.response.status === 401) {
      // 2FA devenue obligatoire pour le rôle (roleGate, token antérieur) : la
      // page de login dira pourquoi la session a sauté et relancera l'activation.
      const code = error.response.data && error.response.data.code
      if (code === 'twofa_required') {
        try { localStorage.setItem('xylo:login-notice', 'twofa_required') } catch (e) { /* ignore */ }
      }
      AUTH_LOCALSTORAGE_KEYS.forEach(k => localStorage.removeItem(k))
      if (typeof window !== 'undefined') {
        // Préfixe de l'app (« /web/ » en prod) : sans lui on partait sur
        // https://xylo.codesinn.com/pages/login, hors de l'application. Et la
        // page demandée part en `to` : un lien reçu par mail (bouton « Encode »
        // du rapport Harvesting) doit y ramener après la connexion.
        const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
        const path = window.location.pathname
        const here = path.startsWith(base) ? path.slice(base.length - 1) : path
        if (!here.startsWith('/pages/')) {
          const to = here + window.location.search
          window.location.href = `${base}pages/login${to && to !== '/' ? `?to=${encodeURIComponent(to)}` : ''}`
        }
      }
    }
    return Promise.reject(error)
  }
)

export default instance
